import React, { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Calendar, CurrencyDollar, Handshake, Phone, FileText, CheckSquare,
  ChatCircleText, CalendarPlus, CalendarCheck, UsersThree, Barbell, ChartLineUp,
  CheckCircle,
} from "@phosphor-icons/react";

// Daily / activity metrics (each can be logged Daily or Weekly via the toggle in the form)
const ACTIVITY_METRICS = [
  { key: "call", label: "Calls", points: 1, color: "#fde68a", icon: Phone, kind: "count" },
  { key: "contact", label: "Contacts", points: 2, color: "#a7f3d0", icon: ChatCircleText, kind: "count" },
  { key: "appt_set_indiv", label: "Appointments Set", points: 5, color: "#bae6fd", icon: CalendarPlus, kind: "count" },
  { key: "referral_received", label: "Referrals Received", points: 10, color: "#ddd6fe", icon: Handshake, kind: "count" },
  { key: "networking_event", label: "Networking Events Attended", points: 10, color: "#fed7aa", icon: UsersThree, kind: "count" },
];

// Production / weekly bonuses + premium / case tracking
const PRODUCTION = [
  { key: "appts_set_week", label: "Appts On The Books", points: 5, color: "#c7d2fe", icon: Calendar, kind: "weekly" },
  { key: "appts_seen_week", label: "Seen On The Books", points: 20, color: "#fbcfe8", icon: CalendarCheck, kind: "weekly" },
  { key: "case_submitted", label: "Case Submitted", points: 10, color: "#fcd34d", icon: FileText, kind: "count" },
  { key: "submitted_premium", label: "Submitted Target Prem.", points: "1pt / $100", color: "#fecaca", icon: ChartLineUp, kind: "premium" },
  { key: "case_paid", label: "Case Paid", points: 20, color: "#86efac", icon: CheckSquare, kind: "count" },
  { key: "paid_premium", label: "Paid Target Prem.", points: "2pts / $100", color: "#fca5a5", icon: CurrencyDollar, kind: "premium" },
  { key: "gdc_logged", label: "Annuity/Investment Commission", points: "tracked", color: "#e9d5ff", icon: CurrencyDollar, kind: "premium" },
  { key: "workout", label: "Workout", points: 5, color: "#bbf7d0", icon: Barbell, kind: "count" },
];

const PRODUCT_LINES = ["Life", "Disability", "LTC", "Annuity", "Investment", "Osaic"];
const PREMIUM_KEYS = new Set(["submitted_premium", "paid_premium", "gdc_logged"]);

export default function LogActivity() {
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Carriers list is no longer needed since legacy sales-pipeline tiles were removed.
  useEffect(() => {}, []);

  const pick = (a) => {
    setPicked(a);
    const now = new Date();
    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    setForm({
      client_name: "", notes: "", premium_amount: "",
      count: a.kind === "count" || a.kind === "weekly" ? "1" : "",
      product_line: PREMIUM_KEYS.has(a.key) ? "Life" : "",
      gdc_mode: a.key === "gdc_logged" ? "case" : "",
      period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
      // Default the Daily|Weekly toggle: weekly bonus types default to weekly,
      // everything else (incl. premium / case) defaults to daily.
      period_kind: a.kind === "weekly" ? "weekly" : "daily",
      logged_date: todayIso, // ET-day; backdatable via the date picker below
      source: "", // Optional sourcing tag — Natural Market / Lead / Referral / Other
    });
  };

  const fireConfetti = (big) => {
    const colors = ["#2563eb", "#34d399", "#fbbf24", "#fb7185", "#09090b"];
    const count = big ? 180 : 60;
    confetti({ particleCount: count, spread: 90, origin: { y: 0.6 }, colors });
    if (big) {
      setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 }, colors }), 150);
      setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 }, colors }), 250);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const sourceRequired = picked?.key !== "workout";
    if (sourceRequired && !form.source) {
      toast.error("Please select a source before saving.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        activity_type: picked.key,
        client_name: form.client_name || null,
        notes: form.notes || null,
        premium_amount: form.premium_amount ? Number(form.premium_amount) : null,
        product_line: form.product_line || null,
        period: picked.key === "gdc_logged" && form.gdc_mode === "monthly" ? form.period : null,
        count: form.count ? Math.max(1, parseInt(form.count, 10) || 1) : 1,
        period_kind: form.period_kind || "daily",
        logged_date: form.logged_date || null,
        source: form.source,
      };
      const { data } = await api.post("/activities", payload);
      const big = ["submitted_premium", "paid_premium", "case_paid", "appts_seen_week"].includes(picked.key);
      fireConfetti(big);
      const verb = form.period_kind === "weekly" ? "weekly tally updated" : "logged";
      toast.success(`+${data.points} PTS · ${picked.label} ${verb}!`);
      setPicked(null);
    } catch (err) {
      toast.error("Could not log activity");
    } finally {
      setSubmitting(false);
    }
  };

  if (picked) {
    const isCount = picked.kind === "count" || picked.kind === "weekly";
    const isPremiumMetric = picked.kind === "premium";
    const isWeekly = form.period_kind === "weekly";

    const previewPts = (() => {
      const c = Math.max(1, parseInt(form.count || "1", 10) || 1);
      const amt = Number(form.premium_amount || 0);
      if (picked.key === "submitted_premium") return Math.floor(amt / 100);
      if (picked.key === "paid_premium") return Math.floor(amt / 100) * 2;
      if (picked.key === "gdc_logged") return 0;
      if (typeof picked.points === "number") return picked.points * c;
      return 0;
    })();

    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={() => setPicked(null)} className="text-sm font-bold uppercase tracking-widest mb-4 hover:underline" data-testid="back-to-picker">← Back</button>
        <motion.form
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="brut p-8 bg-white"
          data-testid="activity-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="border-2 border-black p-3" style={{ background: picked.color }}>
              <picked.icon size={24} weight="bold" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Logging</div>
              <h2 className="font-display font-black text-2xl tracking-tighter">{picked.label} <span className="text-zinc-400 text-base">{typeof picked.points === "number" ? `+${picked.points} pts` : picked.points}</span></h2>
              {isWeekly && <div className="text-xs text-zinc-500 mt-1">One entry per ET week. Re-submitting replaces this week&apos;s count.</div>}
            </div>
          </div>

          <div className="mb-4">
            <Label className="font-bold uppercase tracking-widest text-xs">
              {isWeekly ? "Week Of (any date in the target week)" : "Log Date"}
            </Label>
            <Input
              type="date"
              value={form.logged_date || ""}
              onChange={(e) => setForm({ ...form, logged_date: e.target.value })}
              max={new Date().toISOString().slice(0, 10)}
              className="mt-1 border-2 border-black rounded-none h-10 focus-visible:ring-0 md:w-56"
              data-testid="log-date-input"
            />
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-1">
              Defaults to today (ET). Pick any past date to backdate the entry.
            </div>
          </div>

          {picked.key !== "workout" && (
          <div className="mb-4">
            <Label className="font-bold uppercase tracking-widest text-xs">Source <span className="text-[#fb7185]">*</span></Label>
            <select
              value={form.source || ""}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              required
              className={`mt-1 md:w-56 h-10 border-2 px-3 font-medium bg-white focus:outline-none block ${form.source ? "border-black" : "border-[#fb7185]"}`}
              data-testid="log-source-select"
            >
              <option value="">— Select source —</option>
              <option value="natural_market">Natural Market</option>
              <option value="referral">Referral</option>
              <option value="paid_lead">Paid Lead</option>
              <option value="cold_lead">Cold Lead</option>
              <option value="networking">Networking</option>
            </select>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-1">
              Required — where did this activity come from?
            </div>
          </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Daily | Weekly logging mode — visible for all activity types so reps can
                log either today's hit OR aggregate up the whole week in one entry. */}
            <div className="md:col-span-2">
              <Label className="font-bold uppercase tracking-widest text-xs">Logging Period</Label>
              <div className="mt-2 inline-flex border-2 border-black p-1 bg-white" data-testid="period-kind-toggle">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, period_kind: "daily" })}
                  className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest ${form.period_kind === "daily" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
                  data-testid="period-kind-daily"
                >Daily</button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, period_kind: "weekly" })}
                  className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest ${form.period_kind === "weekly" ? "bg-[#2563eb] text-white" : "text-black hover:bg-zinc-100"}`}
                  data-testid="period-kind-weekly"
                >Weekly</button>
              </div>
              {isWeekly && (
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-2">
                  Mon → Sun (Eastern). Re-submitting replaces this week&apos;s total.
                </div>
              )}
            </div>
            {picked.key === "gdc_logged" && (
              <div className="md:col-span-2">
                <Label className="font-bold uppercase tracking-widest text-xs">Entry Mode</Label>
                <div className="mt-2 inline-flex border-2 border-black p-1 bg-white" data-testid="gdc-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, gdc_mode: "case" })}
                    className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest ${form.gdc_mode === "case" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
                    data-testid="gdc-mode-case"
                  >Per Case</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, gdc_mode: "monthly" })}
                    className={`px-3 py-1.5 font-bold uppercase text-xs tracking-widest ${form.gdc_mode === "monthly" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
                    data-testid="gdc-mode-monthly"
                  >Monthly Total</button>
                </div>
              </div>
            )}
            {picked.key === "gdc_logged" && form.gdc_mode === "monthly" && (
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Statement Month</Label>
                <Input
                  type="month"
                  value={form.period}
                  onChange={(e) => setForm({ ...form, period: e.target.value })}
                  className="mt-2 border-2 border-black rounded-none h-11 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                  data-testid="period-input"
                />
              </div>
            )}
            {isCount && (
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">{isWeekly ? "Total this week" : "How many?"}</Label>
                <Input
                  type="number" min="1" step="1"
                  value={form.count}
                  onChange={(e) => setForm({ ...form, count: e.target.value })}
                  placeholder={isWeekly ? "e.g. 8" : "e.g. 25"}
                  className="mt-2 border-2 border-black rounded-none h-11 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                  data-testid="count-input"
                />
              </div>
            )}
            {isPremiumMetric && (
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Premium $ (this case)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.premium_amount}
                  onChange={(e) => setForm({ ...form, premium_amount: e.target.value })}
                  placeholder="5000"
                  className="mt-2 border-2 border-black rounded-none h-11 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                  data-testid="premium-amount-input"
                />
              </div>
            )}
            {isPremiumMetric && (
              <div>
                <Label className="font-bold uppercase tracking-widest text-xs">Product Line</Label>
                <select
                  value={form.product_line}
                  onChange={(e) => setForm({ ...form, product_line: e.target.value })}
                  className="mt-2 w-full h-11 border-2 border-black px-3 font-medium bg-white"
                  data-testid="product-line-select"
                >
                  {PRODUCT_LINES.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Client (optional)</Label>
              <Input
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="Initials or reference"
                className="mt-2 border-2 border-black rounded-none h-11 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                data-testid="client-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="font-bold uppercase tracking-widest text-xs">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="mt-2 border-2 border-black rounded-none focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                placeholder="Any details…"
                data-testid="notes-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full mt-6 h-14 rounded-none bg-[#2563eb] hover:bg-[#1d4ed8] text-white border-2 border-black font-display font-black text-lg tracking-wide shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 active:shadow-none active:translate-y-1 active:translate-x-1 transition-all"
            data-testid="submit-activity-btn"
          >
            {submitting ? "LOGGING…" : `LOG IT · +${previewPts} PTS`}
          </Button>
        </motion.form>
      </div>
    );
  }

  const Tile = ({ a, i }) => (
    <motion.button
      key={a.key}
      onClick={() => pick(a)}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.03, type: "spring", stiffness: 300 }}
      whileHover={{ y: -3, x: -3 }}
      whileTap={{ y: 3, x: 3, boxShadow: "none" }}
      className="brut p-5 bg-white text-left group"
      data-testid={`activity-tile-${a.key}`}
    >
      <div className="w-12 h-12 border-2 border-black flex items-center justify-center mb-3" style={{ background: a.color }}>
        <a.icon size={24} weight="bold" />
      </div>
      <div className="font-display font-black text-xl tracking-tighter leading-tight">{a.label}</div>
      <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mt-1">
        {typeof a.points === "number" ? `+${a.points} pts${a.kind === "count" ? " each" : ""}` : a.points}
      </div>
      {a.kind === "weekly" && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#fde68a] border-2 border-black px-1.5 py-0.5">
          <CheckCircle size={10} weight="bold" /> Weekly Bonus
        </div>
      )}
    </motion.button>
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Log Activity</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1">What did you do?</h1>
        <p className="text-zinc-600 mt-2">Log daily or weekly. Counts roll into your weekly totals. Confetti included.</p>
      </div>

      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Daily</div>
            <div className="font-display font-black text-2xl tracking-tight">Activity Metrics</div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Per-event · log anytime</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {ACTIVITY_METRICS.map((a, i) => <Tile key={a.key} a={a} i={i} />)}
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Weekly &amp; Production</div>
            <div className="font-display font-black text-2xl tracking-tight">Production</div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Mon → Sun · Eastern Time</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {PRODUCTION.map((a, i) => <Tile key={a.key} a={a} i={i} />)}
        </div>
      </div>
    </div>
  );
}
